import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import { CATEGORY_META, CATEGORY_ORDER } from "../deviceCategories";
import type { DeviceCategory } from "../deviceCategories";
import type { BoardFeedsEntryProps } from "./types";

const total = (types: Record<string, number> | undefined): number =>
  Object.values(types ?? {}).reduce((sum, count) => sum + count, 0);

export const BoardFeedsEntry = ({ feed, typesByCategory, onSave, onCancel }: BoardFeedsEntryProps) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<DeviceCategory[]>(feed.categories);
  const [excludedTypes, setExcludedTypes] = useState<string[]>(feed.excludedTypes);
  const [openCategory, setOpenCategory] = useState<DeviceCategory | null>(null);

  const toggleCategory = (category: DeviceCategory) =>
    setCategories((current) =>
      current.includes(category)
        ? current.filter((one) => one !== category)
        : CATEGORY_ORDER.filter((one) => one === category || current.includes(one)),
    );
  const toggleType = (type: string) =>
    setExcludedTypes((current) =>
      current.includes(type) ? current.filter((one) => one !== type) : [...current, type],
    );

  return (
    <div data-testid="board-feeds-entry" className="mt-2.5 rounded-lg border border-blue-300 bg-white p-2">
      <ul className="flex flex-col">
        {CATEGORY_ORDER.map((category) => {
          const types = typesByCategory[category] ?? {};
          const typeNames = Object.keys(types).filter(Boolean).sort();
          const isChecked = categories.includes(category);
          const isOpen = openCategory === category;
          return (
            <li key={category} className="border-b border-slate-100 last:border-b-0">
              <div className="flex items-center gap-2 px-1 py-1.5">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleCategory(category)}
                  aria-label={t(CATEGORY_META[category].i18nKey)}
                  className="h-4 w-4 flex-none accent-blue-600"
                />
                <span className="min-w-0 flex-1 truncate text-[14px] text-slate-800">
                  {t(CATEGORY_META[category].i18nKey)}
                </span>
                <span className="flex-none text-[12.5px] tabular-nums text-slate-500">
                  {total(types)}
                </span>
                {isChecked && typeNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setOpenCategory(isOpen ? null : category)}
                    aria-expanded={isOpen}
                    aria-label={t("smartSurveyor.cablesBeta.boardFeedsTypes")}
                    className="flex-none rounded p-0.5 text-slate-500 hover:bg-slate-100"
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                )}
              </div>
              {isChecked && isOpen && (
                <ul className="mb-1.5 ml-7 flex flex-col gap-1">
                  {typeNames.map((type) => (
                    <li key={type} className="flex items-center gap-2 text-[13px] text-slate-700">
                      <input
                        type="checkbox"
                        checked={!excludedTypes.includes(type)}
                        onChange={() => toggleType(type)}
                        aria-label={type}
                        className="h-3.5 w-3.5 flex-none accent-blue-600"
                      />
                      <span className="min-w-0 flex-1 truncate">{type}</span>
                      <span className="flex-none tabular-nums text-slate-400">{types[type]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onSave({ categories, excludedTypes })}
          className="rounded-md bg-blue-600 px-3 py-2 text-[14px] font-semibold text-white hover:opacity-90"
        >
          {t("smartSurveyor.cablesBeta.boardFeedsSave")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-2.5 py-2 text-[14px] text-slate-600 hover:bg-slate-100"
        >
          {t("smartSurveyor.cablesBeta.cancel")}
        </button>
      </div>
    </div>
  );
};
